/**
 * Organization Settings — edit details, insurance model, verification.
 */
import { useState, useCallback, useEffect, useMemo } from 'react'
import { View, Text } from 'react-native'
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { notification, NotificationType } from '@/lib/utils/haptics'
import Animated from 'react-native-reanimated'
import { Globe, Phone, Mail, ShieldCheck } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { Badge } from '@/components/ui/Badge'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenState } from '@/components/ui/ScreenState'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import {
  useMyOrganizations,
  useOrganization,
  useOrgMembership,
  useUpdateOrganization,
} from '@/lib/hooks/use-organization'
import { updateOrganizationSchema } from '@/lib/validations/organization.schema'
import { useSemanticColors, SPACING, RADIUS, INTERACTION } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'

export default function OrgSettingsScreen() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { orgId: orgIdParam } = useLocalSearchParams<{ orgId: string }>()

  // Reached without `?orgId`, this screen shimmered forever.
  //
  // `useOrganization(undefined)` is disabled, so the query never runs and sits
  // at data: undefined, isLoading: false, isError: false — and ScreenState
  // falls through to `data === undefined → skeleton`. "This will never load"
  // and "this is loading" are the same picture. The audit measured seven grey
  // bars still shimmering after twenty seconds, in every viewport and theme.
  //
  // The path is not hypothetical: it is what a deep link to
  // tago://hosting/organization/settings lands on — a link that only started
  // arriving here once native deep links were fixed in this same release.
  const { organizations, isLoading: isResolvingOrgs } = useMyOrganizations()
  const orgId = orgIdParam || organizations[0]?.id

  const router = useRouter()
  const { organization, isLoading: isLoadingOrg, isError, refetch } = useOrganization(orgId)
  const { resolved: resolvedMembership, isMember, isManager } = useOrgMembership(orgId)
  const isLoading = isResolvingOrgs || (!!orgId && isLoadingOrg)
  const enter = useEntrance(!isLoading)
  const updateOrg = useUpdateOrganization()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [website, setWebsite] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!organization) return
    setName(organization.name)
    setEmail(organization.email)
    setPhone(organization.phone ?? '')
    setWebsite(organization.website ?? '')
    setErrors({})
  }, [organization])

  const normalizeWebsiteInput = useCallback((value: string): string | undefined => {
    const trimmed = value.trim()
    if (!trimmed) return undefined
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  }, [])

  const clearError = useCallback((field: string) => {
    setErrors((current) => ({ ...current, [field]: '' }))
  }, [])

  const formInput = useMemo(
    () => ({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      website: normalizeWebsiteInput(website),
    }),
    [email, name, normalizeWebsiteInput, phone, website],
  )

  const hasChanges = useMemo(() => {
    if (!organization) return false
    return (
      formInput.name !== organization.name ||
      formInput.email !== organization.email ||
      (formInput.phone ?? '') !== (organization.phone ?? '') ||
      (formInput.website ?? '') !== (organization.website ?? '')
    )
  }, [formInput, organization])

  const canSave = updateOrganizationSchema.safeParse(formInput).success && hasChanges

  const handleSave = useCallback(async () => {
    if (!orgId) return
    const result = updateOrganizationSchema.safeParse(formInput)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0]
        if (typeof field === 'string') fieldErrors[field] = t(issue.message)
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      return
    }

    try {
      await updateOrg.mutateAsync({
        id: orgId,
        input: result.data,
      })
      void notification(NotificationType.Success)
      toast.success(t('common.saved'))
    } catch (err) {
      if (__DEV__) console.error('[OrgSettings] save', err)
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('errors.generic')))
      captureError(err, { screen: 'org_settings', action: 'save' }, { orgId })
    }
  }, [orgId, formInput, updateOrg, t])

  // One container, one JSX position: the loading branch used to re-wrap its
  // own copy. ScreenState owns the region that varies, so a failed org fetch
  // now offers a retry instead of dropping the user into an empty form whose
  // Save button could only fail.
  // Settings for an organization the user does not have is not a loading
  // state, it is the wrong screen. The organization index owns creating and
  // joining one, so send them there rather than to a permanent skeleton.
  if (!isResolvingOrgs && !orgId) {
    return <Redirect href="/(tabs)/hosting/organization" />
  }

  return (
    <ScreenContainer variant="content" scroll={false}>
      {/*
        NO SOS MIEMBRO: decirlo, no dibujar el formulario.

        `organizations_select_verified` deja que CUALQUIER autenticado lea una
        organizacion verificada y activa, asi que con un `?orgId=` prestado esta
        pantalla se dibujaba entera --con la razon social y el NIT reales de esa
        empresa precargados-- y el rechazo llegaba recien al guardar, en
        `assertManager`. RLS no filtraba nada porque la politica lo permite: el
        defecto es que la pantalla no preguntaba de quien es.

        LA ELECCION VA ACA ADENTRO, no en returns hermanos. Un `ScreenContainer`
        por rama remonta la pantalla entera en cada transicion, y
        `validate-app-snappiness` lo prohibe -- me lo canto en la primera
        corrida. Es la misma forma que usa `hosting/payouts/onboarding.tsx`.

        FALLA ABIERTO: mientras `resolvedMembership` sea false no se dibuja
        ningun muro, asi que un administrador legitimo con la red lenta ve la
        pantalla de siempre.
      */}
      {resolvedMembership && !isMember ? (
        <>
          <ScreenHeader title={t('org.settings_title')} backTestID="org.settings-back" />
          <EmptyState
            testID="org.settings-not-member"
            title={t('org.not_member_title')}
            description={t('org.not_member_body')}
            action={{
              label: t('org.not_member_action'),
              onPress: () => router.replace('/(tabs)/hosting/organization' as never),
            }}
          />
        </>
      ) : resolvedMembership && !isManager ? (
        <>
          <ScreenHeader title={t('org.settings_title')} backTestID="org.settings-back" />
          <EmptyState
            testID="org.settings-not-manager"
            title={t('org.not_manager_title')}
            description={t('org.not_manager_body')}
            action={{
              label: t('org.not_manager_action'),
              onPress: () =>
                router.replace(`/hosting/organization/members?orgId=${orgId}` as never),
            }}
          />
        </>
      ) : (
        <ScreenState
          query={{ data: organization, isLoading, isError, refetch }}
          skeleton={
            // The chrome that does not depend on the query renders for real:
            // header, back button and the screen title. Four grey bars under a
            // lone back circle told the user nothing about what was loading.
            // Only the body is skeletonised, and it mirrors the real layout
            // (verification banner → legal card → four fields → save) so nothing
            // jumps when the org arrives and the placeholder fills the viewport
            // instead of stopping two thirds up.
            <View style={{ flex: 1, padding: SPACING.lg }}>
              <ScreenHeader
                backTestID="org.settings-back"
                style={{ paddingHorizontal: 0, paddingTop: 0, paddingBottom: SPACING.md }}
              />
              <Text
                style={{
                  fontSize: 22,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  letterSpacing: -0.3,
                  marginBottom: SPACING.lg,
                }}
              >
                {t('org.settings')}
              </Text>
              <View style={{ flex: 1, gap: SPACING.md }}>
                <Skeleton width="100%" height={64} borderRadius={RADIUS.md} />
                <Skeleton width="100%" height={132} borderRadius={RADIUS.lg} staggerIndex={1} />
                {/* Same field height as the real form controls, so nothing below
                  the first field moves when the org loads. */}
                {[0, 1, 2, 3].map((field) => (
                  <Skeleton
                    key={field}
                    width="100%"
                    height={INTERACTION.fieldHeight}
                    borderRadius={RADIUS.md}
                    staggerIndex={field + 2}
                  />
                ))}
                <View style={{ flex: 1, justifyContent: 'flex-end' }}>
                  <Skeleton
                    width="100%"
                    height={INTERACTION.fieldHeight}
                    borderRadius={RADIUS.lg}
                    staggerIndex={6}
                  />
                </View>
              </View>
            </View>
          }
        >
          {() => (
            <KeyboardAwareView
              includeTopInset={false}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{
                padding: SPACING.lg,
                paddingBottom: SPACING['3xl'] + SPACING['2xl'],
              }}
            >
              <ScreenHeader
                backTestID="org.settings-back"
                style={{ paddingHorizontal: 0, paddingTop: 0, paddingBottom: SPACING.md }}
              />

              {/* Header */}
              <Animated.View
                collapsable={false}
                entering={enter(1)}
                style={{ marginBottom: SPACING.lg }}
              >
                <Text
                  style={{
                    fontSize: 22,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                    letterSpacing: -0.3,
                  }}
                >
                  {t('org.settings')}
                </Text>
              </Animated.View>

              {/* Verification banner */}
              <Animated.View collapsable={false} entering={enter(2)}>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: SPACING.md,
                    borderRadius: RADIUS.md,
                    backgroundColor: organization?.isVerified
                      ? `${colors.success}12`
                      : `${colors.warning}12`,
                    marginBottom: SPACING.lg,
                    gap: SPACING.sm,
                  }}
                >
                  <ShieldCheck
                    size={22}
                    color={organization?.isVerified ? colors.success : colors.warning}
                    strokeWidth={1.5}
                  />
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontSize: 14,
                        fontFamily: FONTS.bodyBold,
                        color: organization?.isVerified ? colors.success : colors.warning,
                      }}
                    >
                      {organization?.isVerified ? t('org.verified') : t('org.verification_pending')}
                    </Text>
                    {!organization?.isVerified && (
                      <Text
                        style={{
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                          marginTop: 2,
                        }}
                      >
                        {t('org.verification_desc')}
                      </Text>
                    )}
                  </View>
                  <Badge
                    label={organization?.isVerified ? t('common.verified') : t('common.pending')}
                    variant={organization?.isVerified ? 'success' : 'warning'}
                    size="sm"
                  />
                </View>
              </Animated.View>

              {/* Legal info (read-only) */}
              <Animated.View
                collapsable={false}
                entering={enter(3)}
                style={{
                  backgroundColor: colors.surface,
                  borderRadius: RADIUS.lg,
                  padding: SPACING.md,
                  marginBottom: SPACING.lg,
                  ...WEB_SHADOWS.sm,
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontFamily: FONTS.body,
                    marginBottom: SPACING.sm,
                  }}
                >
                  {t('org.legal_info')}
                </Text>
                <View style={{ gap: SPACING.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text
                      style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}
                    >
                      {t('org.legal_name')}
                    </Text>
                    <Text
                      style={{
                        fontSize: 14,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                        flexShrink: 1,
                        textAlign: 'right',
                      }}
                      numberOfLines={1}
                    >
                      {organization?.legalName ?? '—'}
                    </Text>
                  </View>
                  <View
                    style={{
                      height: 1,
                      backgroundColor: colors.border,
                      marginVertical: SPACING.xs,
                    }}
                  />
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text
                      style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}
                    >
                      {t('org.tax_id')}
                    </Text>
                    <Text
                      style={{
                        fontSize: 14,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {organization?.taxId ?? '—'}
                    </Text>
                  </View>
                  <View
                    style={{
                      height: 1,
                      backgroundColor: colors.border,
                      marginVertical: SPACING.xs,
                    }}
                  />
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text
                      style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}
                    >
                      {t('org.insurance_model')}
                    </Text>
                    <Badge
                      label={t(`org.insurance_${organization?.insuranceModel ?? 'platform'}`)}
                      variant="secondary"
                      size="sm"
                    />
                  </View>
                </View>
              </Animated.View>

              {/* Editable fields */}
              <Animated.View collapsable={false} entering={enter(4)} style={{ gap: SPACING.md }}>
                <TextInput
                  label={t('org.name')}
                  value={name}
                  onChangeText={(value) => {
                    setName(value)
                    clearError('name')
                  }}
                  error={errors.name}
                  testID="org.settings-name"
                />
                <TextInput
                  label={t('org.email')}
                  value={email}
                  onChangeText={(value) => {
                    setEmail(value)
                    clearError('email')
                  }}
                  error={errors.email}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                  leftIcon={<Mail size={18} color={colors.textMuted} strokeWidth={1.5} />}
                  testID="org.settings-email"
                />
                <TextInput
                  label={`${t('org.phone')} (${t('common.optional')})`}
                  value={phone}
                  onChangeText={(value) => {
                    setPhone(value)
                    clearError('phone')
                  }}
                  error={errors.phone}
                  keyboardType="phone-pad"
                  inputMode="tel"
                  autoComplete="tel"
                  leftIcon={<Phone size={18} color={colors.textMuted} strokeWidth={1.5} />}
                  testID="org.settings-phone"
                />
                <TextInput
                  label={`${t('org.website')} (${t('common.optional')})`}
                  value={website}
                  onChangeText={(value) => {
                    setWebsite(value)
                    clearError('website')
                  }}
                  error={errors.website}
                  keyboardType="url"
                  inputMode="url"
                  autoCapitalize="none"
                  autoComplete="url"
                  leftIcon={<Globe size={18} color={colors.textMuted} strokeWidth={1.5} />}
                  testID="org.settings-website"
                />
              </Animated.View>

              {/* Save */}
              <Animated.View
                collapsable={false}
                entering={enter(5)}
                style={{ marginTop: SPACING.xl }}
              >
                <Button
                  title={t('common.save')}
                  onPress={handleSave}
                  loading={updateOrg.isPending}
                  disabled={!canSave || updateOrg.isPending}
                  variant="primary"
                  size="lg"
                  testID="org.settings-save"
                />
              </Animated.View>
            </KeyboardAwareView>
          )}
        </ScreenState>
      )}
    </ScreenContainer>
  )
}
